import type {
	API_CreateScheduleRequest,
	API_CreateScheduleResponse,
	API_ListOrganizationSchedulesPaginatedResponse,
	API_ListOrganizationSchedulesResponse,
	API_ListScheduleSessionsWithRelationsResponse,
	API_MigrateSchedulesRequest,
	API_MigrateSchedulesResponse,
	API_UpdateScheduleRequest,
	API_UpdateScheduleResponse,
	DayOfWeek,
	FE_ScheduleSession,
	PaginationRequest,
	Schedule,
	ScheduleType,
} from "@pilatestime/shared-types";
import { queryOptions, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useMutationWithToast } from "@/hooks/useMutationWithToast";
import { convertScheduleSessionToEventSession } from "@/utils/attendanceUtils";
import { translateBackendError } from "@/utils/errorTranslation";
import api from "./api_client";

// API functions
export const fetchOrganizationSchedules = (
	token: string,
	organizationId: string,
): Promise<API_ListOrganizationSchedulesResponse> =>
	api
		.get(`/organizations/${organizationId}/schedules`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

// Filter params type for schedule list
export interface ScheduleFilterParams {
	staffMemberId?: string;
	type?: ScheduleType;
	isActive?: boolean;
	daysOfWeek?: DayOfWeek[];
}

// Paginated schedule list
export const fetchOrganizationSchedulesPaginated = (
	token: string,
	organizationId: string,
	paginationParams: PaginationRequest,
	searchTerm?: string,
	filterParams?: ScheduleFilterParams,
): Promise<API_ListOrganizationSchedulesPaginatedResponse> =>
	api
		.get(`/organizations/${organizationId}/schedules/paginated`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
			params: {
				...paginationParams,
				...(searchTerm && { search: searchTerm }),
				...(filterParams?.staffMemberId && {
					staffMemberId: filterParams.staffMemberId,
				}),
				...(filterParams?.type && { type: filterParams.type }),
				...(filterParams?.isActive !== undefined && {
					isActive: filterParams.isActive,
				}),
				...(filterParams?.daysOfWeek &&
					filterParams.daysOfWeek.length > 0 && {
						daysOfWeek: filterParams.daysOfWeek.join(","),
					}),
			},
		})
		.then((res) => res.data);

export const fetchScheduleById = (
	token: string,
	organizationId: string,
	scheduleId: string,
): Promise<Schedule> =>
	api
		.get(`/organizations/${organizationId}/schedules/${scheduleId}`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const fetchSchedulesByStaffMember = (
	token: string,
	organizationId: string,
	staffId: string,
	options?: { hasActiveFutureSessions?: boolean },
): Promise<API_ListOrganizationSchedulesResponse> =>
	api
		.get(`/organizations/${organizationId}/staff/${staffId}/schedules`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
			params: {
				...(options?.hasActiveFutureSessions && {
					hasActiveFutureSessions: "true",
				}),
			},
		})
		.then((res) => res.data);

export const createSchedule = (
	token: string,
	organizationId: string,
	data: API_CreateScheduleRequest,
): Promise<API_CreateScheduleResponse> =>
	api
		.post(`/organizations/${organizationId}/schedules`, data, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const updateSchedule = (
	token: string,
	organizationId: string,
	scheduleId: string,
	data: API_UpdateScheduleRequest,
): Promise<API_UpdateScheduleResponse> =>
	api
		.put(`/organizations/${organizationId}/schedules/${scheduleId}`, data, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const deleteSchedule = (
	token: string,
	organizationId: string,
	scheduleId: string,
): Promise<void> =>
	api
		.delete(`/organizations/${organizationId}/schedules/${scheduleId}`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

// Query options
export const fetchOrganizationSchedulesQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
) =>
	queryOptions({
		queryKey: ["organization_schedules", organizationId, userId],
		queryFn: () => fetchOrganizationSchedules(token, organizationId),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

export const fetchOrganizationSchedulesPaginatedQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
	paginationParams: PaginationRequest,
	searchTerm?: string,
	filterParams?: ScheduleFilterParams,
) =>
	queryOptions({
		queryKey: [
			"organization_schedules_paginated",
			organizationId,
			userId,
			paginationParams,
			searchTerm ?? "",
			filterParams?.staffMemberId ?? "",
			filterParams?.type ?? "",
			filterParams?.isActive ?? "",
			filterParams?.daysOfWeek ?? [],
		],
		queryFn: () =>
			fetchOrganizationSchedulesPaginated(
				token,
				organizationId,
				paginationParams,
				searchTerm,
				filterParams,
			),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

export const fetchScheduleByIdQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
	scheduleId: string,
) =>
	queryOptions({
		queryKey: ["schedule", organizationId, scheduleId, userId],
		queryFn: () => fetchScheduleById(token, organizationId, scheduleId),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

export const fetchSchedulesByStaffMemberQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
	staffId: string,
	options?: { hasActiveFutureSessions?: boolean },
) =>
	queryOptions({
		queryKey: [
			"staff_schedules",
			organizationId,
			staffId,
			userId,
			options?.hasActiveFutureSessions ?? false,
		],
		queryFn: () =>
			fetchSchedulesByStaffMember(token, organizationId, staffId, options),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

// Mutation hooks
export const useCreateSchedule = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			data,
		}: {
			token: string;
			organizationId: string;
			data: API_CreateScheduleRequest;
		}) => createSchedule(token, organizationId, data),
		toastMessages: {
			loading: t("schedules.toasts.creating"),
			success: t("schedules.toasts.createSuccess"),
			error: t("schedules.toasts.createError"),
		},
		showErrorToast: false, // Disable automatic error toast to handle manually
		onSuccess: (_data, variables) => {
			// Invalidate and refetch organization schedules
			queryClient.invalidateQueries({
				queryKey: ["organization_schedules", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: [
					"organization_schedules_paginated",
					variables.organizationId,
				],
			});
			// Invalidate staff schedules (for staff detail page schedules tab)
			queryClient.invalidateQueries({
				queryKey: ["staff_schedules", variables.organizationId],
			});
		},
		onError: (error: AxiosError) => {
			// Handle specific error cases
			const errorData = error.response?.data as { message?: string };
			const specificMessage = errorData?.message;

			if (specificMessage) {
				// Translate the backend error message to the user's language
				const translatedMessage = translateBackendError(specificMessage, t);
				toast.error(translatedMessage);
			} else {
				// For all other errors, show the generic error message
				toast.error(t("schedules.toasts.createError"));
			}
		},
	});
};

export const useUpdateSchedule = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			scheduleId,
			data,
		}: {
			token: string;
			organizationId: string;
			scheduleId: string;
			data: API_UpdateScheduleRequest;
		}) => updateSchedule(token, organizationId, scheduleId, data),
		toastMessages: {
			loading: t("schedules.toasts.updating"),
			success: t("schedules.toasts.updateSuccess"),
			error: t("schedules.toasts.updateError"),
		},
		showErrorToast: false, // Disable automatic error toast to handle manually
		onSuccess: (_data, variables) => {
			// Invalidate and refetch organization schedules
			queryClient.invalidateQueries({
				queryKey: ["organization_schedules", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: [
					"organization_schedules_paginated",
					variables.organizationId,
				],
			});
			// Invalidate specific schedule query
			queryClient.invalidateQueries({
				queryKey: ["schedule", variables.organizationId, variables.scheduleId],
			});
			// Invalidate staff schedules (for staff detail page schedules tab)
			queryClient.invalidateQueries({
				queryKey: ["staff_schedules", variables.organizationId],
			});
			// Invalidate calendar/attendance queries to refresh schedule sessions
			queryClient.invalidateQueries({
				queryKey: ["scheduleSessions", "list", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["scheduleSessions", "monthly", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["attendance", "list"],
			});
		},
		onError: (error: AxiosError) => {
			// Handle specific error cases
			const errorData = error.response?.data as { message?: string };
			const specificMessage = errorData?.message;

			if (specificMessage) {
				// Translate the backend error message to the user's language
				const translatedMessage = translateBackendError(specificMessage, t);
				toast.error(translatedMessage);
			} else {
				// For all other errors, show the generic error message
				toast.error(t("schedules.toasts.updateError"));
			}
		},
	});
};

export const useDeleteSchedule = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			scheduleId,
		}: {
			token: string;
			organizationId: string;
			scheduleId: string;
		}) => deleteSchedule(token, organizationId, scheduleId),
		toastMessages: {
			loading: t("schedules.toasts.deleting"),
			success: t("schedules.toasts.deleteSuccess"),
			error: t("schedules.toasts.deleteError"),
		},
		onSuccess: (_data, variables) => {
			// Invalidate and refetch organization schedules
			queryClient.invalidateQueries({
				queryKey: ["organization_schedules", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: [
					"organization_schedules_paginated",
					variables.organizationId,
				],
			});
			// Invalidate staff schedules (for staff detail page schedules tab)
			queryClient.invalidateQueries({
				queryKey: ["staff_schedules", variables.organizationId],
			});
			// Remove specific schedule from cache
			queryClient.removeQueries({
				queryKey: ["schedule", variables.organizationId, variables.scheduleId],
			});
		},
	});
};

// Schedule Migration API functions
export const migrateSchedules = (
	token: string,
	organizationId: string,
	sourceStaffId: string,
	data: API_MigrateSchedulesRequest,
): Promise<API_MigrateSchedulesResponse> =>
	api
		.post(
			`/organizations/${organizationId}/staff/${sourceStaffId}/schedules/migrate`,
			data,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => res.data);

export const useMigrateSchedules = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			sourceStaffId,
			data,
		}: {
			token: string;
			organizationId: string;
			sourceStaffId: string;
			data: API_MigrateSchedulesRequest;
		}) => migrateSchedules(token, organizationId, sourceStaffId, data),
		toastMessages: {
			loading: t("staff.migrateSchedules.toasts.migrating"),
			success: t("staff.migrateSchedules.toasts.migrateSuccess"),
			error: t("staff.migrateSchedules.toasts.migrateError"),
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["staff_schedules", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["organization_schedules", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: [
					"organization_schedules_paginated",
					variables.organizationId,
				],
			});
			queryClient.invalidateQueries({
				queryKey: ["scheduleSessions", "list", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["scheduleSessions", "monthly", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["attendance", "list"],
			});
			queryClient.invalidateQueries({
				queryKey: ["schedule", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["organization_staff", variables.organizationId],
			});
		},
	});
};

// Schedule Session API functions
export const fetchScheduleSession = async (
	organizationId: string,
	sessionId: string,
): Promise<FE_ScheduleSession> => {
	// Use the list endpoint with a filter to get the single session with relations and attendance
	const params = new URLSearchParams();
	params.append("sessionId", sessionId);

	const response = await api.get<API_ListScheduleSessionsWithRelationsResponse>(
		`/organizations/${organizationId}/schedule-sessions?${params.toString()}`,
	);

	// Find the specific session (should be the only one returned)
	const session = response.data.sessions.find((s) => s.id === sessionId);
	if (!session) {
		throw new Error(`Session ${sessionId} not found`);
	}

	return convertScheduleSessionToEventSession(session, session.attendanceCount);
};

// Schedule Session Query options
export const fetchScheduleSessionQueryOptions = (
	organizationId: string,
	sessionId: string,
) =>
	queryOptions({
		queryKey: ["scheduleSessions", "detail", organizationId, sessionId],
		queryFn: () => fetchScheduleSession(organizationId, sessionId),
		enabled: !!organizationId && !!sessionId,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});
